// Lightweight markdown helpers shared by blog list/post pages.
import { marked } from "marked";
import xss from "xss";

marked.setOptions({ gfm: true, breaks: false });

// DOM-free sanitizer: runs identically on the server (Workers runtime, no DOM)
// and in the browser, so SSR output and hydration always match.
const SANITIZE_OPTIONS = {
  whiteList: {
    a: ["href", "title", "target", "rel"],
    p: [], br: [], hr: [],
    h1: ["id"], h2: ["id"], h3: ["id"], h4: ["id"], h5: ["id"], h6: ["id"],
    strong: [], em: [], b: [], i: [], u: [], s: [], del: [], mark: [], small: [], sub: [], sup: [],
    ul: [], ol: ["start"], li: [],
    blockquote: [], code: ["class"], pre: ["class"],
    span: ["id", "class"], div: ["class"],
    img: ["src", "alt", "title", "width", "height", "loading"],
    table: [], thead: [], tbody: [], tfoot: [], tr: [], th: ["colspan", "rowspan", "align"], td: ["colspan", "rowspan", "align"],
    figure: [], figcaption: [],
  } as Record<string, string[]>,
  stripIgnoreTag: true,
  stripIgnoreTagBody: ["script", "style"],
};

export type Heading = { id: string; text: string; level: 2 | 3 };

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

export function extractHeadings(md: string): Heading[] {
  const out: Heading[] = [];
  const seen = new Map<string, number>();
  for (const line of md.split("\n")) {
    const m = /^(#{2,3})\s+(.+?)\s*$/.exec(line);
    if (!m) continue;
    const level = (m[1].length as 2 | 3);
    const text = m[2].replace(/[*_`]/g, "");
    let id = slugify(text);
    const n = seen.get(id) ?? 0;
    seen.set(id, n + 1);
    if (n > 0) id = `${id}-${n}`;
    out.push({ id, text, level });
  }
  return out;
}

// Render markdown to sanitized HTML, adding ids to h2/h3 so the TOC anchors.
// Sanitization runs on both server and client with a DOM-free sanitizer, so the
// SSR HTML sent to visitors is already stripped of dangerous markup.
export function renderMarkdown(md: string): string {
  const tokens = marked.lexer(md);
  const seen = new Map<string, number>();
  for (const t of tokens) {
    if (t.type === "heading" && (t.depth === 2 || t.depth === 3)) {
      const text = (t as any).text as string;
      let id = slugify(text);
      const n = seen.get(id) ?? 0;
      seen.set(id, n + 1);
      if (n > 0) id = `${id}-${n}`;
      (t as any).text = `<span id="${id}"></span>${text}`;
    }
  }
  const html = marked.parser(tokens);
  return xss(html, SANITIZE_OPTIONS);
}
